import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions, Button, Chip, Stack, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Avatar, Tabs, Tab, MenuItem, Select, InputLabel, FormControl, CircularProgress, Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Preview as PreviewIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Image as ImageIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import {
  getPostTemplates,
  createPostTemplate,
  updatePostTemplate,
  deletePostTemplate,
  publishPost,
  schedulePost,
} from '../../api/postTemplate.api.js';

const platformIcons = {
  facebook: <FacebookIcon color="primary" />,
  twitter: <TwitterIcon color="info" />,
  linkedin: <LinkedInIcon color="primary" />,
};

const brandColor = '#1976d2';

const DEFAULT_TEMPLATES = [
  {
    _id: 'mock-1',
    name: 'Product Launch',
    content: 'We are excited to announce our new product! Check it out now.',
    platforms: ['facebook', 'twitter', 'linkedin'],
    image: '',
  },
  {
    _id: 'mock-2',
    name: 'Event Announcement',
    content: 'Join us for our upcoming event. RSVP today!',
    platforms: ['facebook', 'linkedin'],
    image: '',
  },
  {
    _id: 'mock-3',
    name: 'Weekly Update',
    content: 'Here are this week’s highlights and updates from our team.',
    platforms: ['twitter', 'linkedin'],
    image: '',
  },
];

function PostBuilder() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [post, setPost] = useState({
    content: '',
    image: '',
    platforms: ['facebook'],
    scheduledAt: '',
  });
  const [activePreview, setActivePreview] = useState('facebook');
  const [aiLoading, setAiLoading] = useState(false);
  const [publishStatus, setPublishStatus] = useState('');
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    content: '',
    image: '',
    platforms: ['facebook'],
    variables: [],
  });
  const [publishing, setPublishing] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPostTemplates();
      if (Array.isArray(data) && data.length > 0) {
        setTemplates(data);
      } else {
        setTemplates(DEFAULT_TEMPLATES);
      }
    } catch (err) {
      setTemplates(DEFAULT_TEMPLATES);
      setError('Failed to load templates, using defaults.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    setPost({
      content: template.content,
      image: template.image,
      platforms: template.platforms,
      scheduledAt: '',
    });
    setEditorOpen(true);
    setActivePreview(template.platforms[0] || 'facebook');
  };

  const handleAiSuggest = async () => {
    setAiLoading(true);
    setTimeout(() => {
      setPost((p) => ({ ...p, content: `${p.content} (AI suggestion: Add a call to action!)` }));
      setAiLoading(false);
    }, 1200);
  };

  const handlePublish = async () => {
    if (!selectedTemplate?._id) return;
    setPublishing(true);
    setError('');
    try {
      await publishPost(selectedTemplate._id, {
        content: post.content,
        image: post.image,
        platforms: post.platforms,
      });
      setPublishStatus('Published!');
      setEditorOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setPublishing(false);
      setTimeout(() => setPublishStatus(''), 2000);
    }
  };
  const handleSchedule = async () => {
    if (!selectedTemplate?._id) return;
    setPublishing(true);
    setError('');
    try {
      await schedulePost(selectedTemplate._id, {
        content: post.content,
        image: post.image,
        platforms: post.platforms,
        scheduledAt: post.scheduledAt,
      });
      setPublishStatus('Scheduled!');
      setEditorOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setPublishing(false);
      setTimeout(() => setPublishStatus(''), 2000);
    }
  };

  const handleOpenTemplateDialog = (template = null) => {
    setEditMode(!!template);
    setTemplateForm(template ? { ...template } : {
      name: '',
      description: '',
      content: '',
      image: '',
      platforms: ['facebook'],
      variables: [],
    });
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    setLoading(true);
    setError('');
    try {
      if (editMode && templateForm._id) {
        await updatePostTemplate(templateForm._id, templateForm);
      } else {
        await createPostTemplate(templateForm);
      }
      setTemplateDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    setLoading(true);
    setError('');
    try {
      await deletePostTemplate(templateId);
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        Social Media Post & Ad Builder
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Select a template or start from scratch. Customize your post, preview for each platform, and schedule or publish instantly.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {templates.map((template, idx) => (
            <Grid item xs={12} sm={6} md={4} key={template._id || idx}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Avatar sx={{
                      bgcolor: brandColor, color: '#fff', width: 32, height: 32,
                    }}
                    >
                      <ImageIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600}>{template.name}</Typography>
                      <Stack direction="row" spacing={1}>
                        {template.platforms.map((p) => (
                          <Tooltip title={p.charAt(0).toUpperCase() + p.slice(1)} key={p}>{platformIcons[p]}</Tooltip>
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{template.description}</Typography>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Content:</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>{template.content}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Variables:
                    {template.variables?.join(', ')}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Button size="small" onClick={() => handleUseTemplate(template)} startIcon={<SendIcon />}>Use Template</Button>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenTemplateDialog(template)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template._id)}><DeleteIcon /></IconButton></Tooltip>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{
              height: '100%', display: 'flex', flexDirection: 'column', border: '2px dashed #ccc', justifyContent: 'center', alignItems: 'center', minHeight: 200,
            }}
            >
              <Button startIcon={<AddIcon />} onClick={() => handleOpenTemplateDialog(null)}>
                Create New Template
              </Button>
            </Card>
          </Grid>
        </Grid>
      )}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: brandColor, color: '#fff' }}>
          Edit Post / Ad
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Platforms</InputLabel>
              <Select
                multiple
                value={post.platforms}
                onChange={(e) => setPost((p) => ({ ...p, platforms: e.target.value }))}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={1}>{selected.map((p) => platformIcons[p])}</Stack>
                )}
              >
                <MenuItem value="facebook">
                  <Stack direction="row" spacing={1}>
                    <FacebookIcon />
                    {' '}
                    Facebook
                  </Stack>
                </MenuItem>
                <MenuItem value="twitter">
                  <Stack direction="row" spacing={1}>
                    <TwitterIcon />
                    {' '}
                    Twitter
                  </Stack>
                </MenuItem>
                <MenuItem value="linkedin">
                  <Stack direction="row" spacing={1}>
                    <LinkedInIcon />
                    {' '}
                    LinkedIn
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Post Content"
              value={post.content}
              onChange={(e) => setPost((p) => ({ ...p, content: e.target.value }))}
              fullWidth
              multiline
              minRows={4}
              helperText="Use variables like {{product_name}}, {{event_date}}, etc."
            />
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleAiSuggest}
              disabled={aiLoading}
            >
              {aiLoading ? 'Suggesting...' : 'AI Suggest Text'}
            </Button>
            <TextField
              label="Image URL (optional)"
              value={post.image}
              onChange={(e) => setPost((p) => ({ ...p, image: e.target.value }))}
              fullWidth
              helperText="Paste an image URL or leave blank."
            />
            <TextField
              label="Schedule (optional)"
              type="datetime-local"
              value={post.scheduledAt}
              onChange={(e) => setPost((p) => ({ ...p, scheduledAt: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Tabs value={activePreview} onChange={(_, v) => setActivePreview(v)}>
              {post.platforms.map((p) => (
                <Tab key={p} value={p} icon={platformIcons[p]} label={p.charAt(0).toUpperCase() + p.slice(1)} />
              ))}
            </Tabs>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 120, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview:
                {activePreview.charAt(0).toUpperCase() + activePreview.slice(1)}
              </Typography>
              {post.image && <img src={post.image} alt="preview" style={{ maxWidth: 200, maxHeight: 120, marginBottom: 8 }} />}
              <Typography variant="body1" sx={{ color: brandColor }}>{post.content}</Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handlePublish} startIcon={<SendIcon />} disabled={publishing}>Publish Now</Button>
          <Button variant="outlined" color="secondary" onClick={handleSchedule} startIcon={<ScheduleIcon />} disabled={publishing}>Schedule</Button>
        </DialogActions>
        {publishing && <Box sx={{ p: 2, textAlign: 'center', color: brandColor }}><CircularProgress size={24} /></Box>}
        {publishStatus && <Box sx={{ p: 2, textAlign: 'center', color: brandColor }}>{publishStatus}</Box>}
      </Dialog>
      {/* Template Create/Edit Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Template' : 'Create Template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Template Name"
              value={templateForm.name}
              onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={templateForm.description}
              onChange={(e) => setTemplateForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Platforms</InputLabel>
              <Select
                multiple
                value={templateForm.platforms}
                onChange={(e) => setTemplateForm((f) => ({ ...f, platforms: e.target.value }))}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={1}>{selected.map((p) => platformIcons[p])}</Stack>
                )}
              >
                <MenuItem value="facebook">
                  <Stack direction="row" spacing={1}>
                    <FacebookIcon />
                    {' '}
                    Facebook
                  </Stack>
                </MenuItem>
                <MenuItem value="twitter">
                  <Stack direction="row" spacing={1}>
                    <TwitterIcon />
                    {' '}
                    Twitter
                  </Stack>
                </MenuItem>
                <MenuItem value="linkedin">
                  <Stack direction="row" spacing={1}>
                    <LinkedInIcon />
                    {' '}
                    LinkedIn
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Content"
              value={templateForm.content}
              onChange={(e) => setTemplateForm((f) => ({ ...f, content: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              required
            />
            <TextField
              label="Image URL (optional)"
              value={templateForm.image}
              onChange={(e) => setTemplateForm((f) => ({ ...f, image: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Variables (comma separated)"
              value={(templateForm.variables || []).join(', ')}
              onChange={(e) => setTemplateForm((f) => ({ ...f, variables: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) }))}
              fullWidth
              helperText="e.g., {{product_name}}, {{event_date}}"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplate}>{editMode ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PostBuilder;
